import { useEffect, useRef } from "react";
import { PlusIcon } from "../components/icons";
import Spinner from "../components/Spinner";

const TILE_KEYS = ["Lead Created", "Application Created", "In Bank Process"];
const TILE_BG = {
  "Lead Created": "#C9F0FA",
  "Application Created": "#C7F2D7",
  "In Bank Process": "#E4DBFB",
  "Reportee Applications": "#E8F0FE",
};

function renderTile(key, t, filter, onFilterToggle) {
  return (
    <button
      key={key}
      type="button"
      className={"tile" + (filter === key ? " tile--active" : "")}
      style={{ background: TILE_BG[key] }}
      onClick={() => onFilterToggle(key)}
    >
      <span className="tile__n">{t.count}</span>
      <span className="tile__label">{t.label}</span>
      <span className="tile__arrow">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1F1F1F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="13 6 19 12 13 18"></polyline>
        </svg>
      </span>
    </button>
  );
}

// status_id: 1 Application · 2 Sent to Financier · 3 Financier Approval · 4 Contract Signed
const pillBg = (statusId, isComplete) =>
  isComplete
    ? "#F5A623"
    : statusId === 1
    ? "#05AC56"
    : statusId === 2
    ? "#7B61FF"
    : statusId === 3
    ? "#23B0A0"
    : statusId === 4
    ? "#2F80F4"
    : statusId === 7
    ? "#D64545"
    : "#0BBDDD";

// Which post-application stage screen to land on when tracking a sent lead.
export const statusStage = (statusId) => (statusId >= 3 ? "contract" : statusId === 2 ? "noa" : "send");

export default function DashboardPage({
  user,
  leads,
  leadsLoading,
  hasMoreLeads,
  loadingMoreLeads,
  onLoadMoreLeads,
  leadsTotal,
  buckets,
  q,
  filter,
  onSearch,
  onFilterToggle,
  onCreateApplication,
  onOpenLead,
  onContinueLead,
  onLogout,
}) {
  const displayName = user?.name || user?.display_name || "there";
  const roleLine = [user?.roleName, user?.business_unit].filter(Boolean).join(" · ");

  // Bucket counts are true DB counts (unaffected by the list's pagination) —
  // "All" is every lead across the 3 buckets, a selected tile is just its own
  // count. A search narrows further than any bucket can express, so that case
  // falls back to the server's totalrecords for the current search instead.
  const bucketTotal =
    filter === "all" ? TILE_KEYS.reduce((sum, key) => sum + (buckets[key]?.count || 0), 0) : buckets[filter]?.count ?? 0;
  const leadCount = q.trim() ? leadsTotal : bucketTotal;

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMoreLeads) return;
    const observer = new IntersectionObserver((entries) => entries[0].isIntersecting && onLoadMoreLeads(), {
      rootMargin: "200px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMoreLeads, onLoadMoreLeads]);

  return (
    <div className="page-shell">
      <div className="dash-topbar">
        <div className="container">
          <div className="dash-topbar__row">
            <span className="dash-avatar">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                <circle cx="12" cy="8" r="4"></circle>
                <path d="M4 22a8 8 0 0 1 16 0z"></path>
              </svg>
            </span>
            <div className="dash-identity">
              <div className="dash-identity__name">Hi {displayName}</div>
              {roleLine && <div className="dash-identity__role">{roleLine}</div>}
            </div>
            <button type="button" className="logout-btn" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      <div style={{ flex: "1 0 auto" }}>
        <div className="container">
          <div className="dash-search">
            <input placeholder="Search by Loan ID, Customer Name" value={q} onChange={(e) => onSearch(e.target.value)} />
          </div>

          <div className="dash-banner">
            <span className="dash-banner__icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0BBDDD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 21h16"></path>
                <path d="M5 21V9l7-5 7 5v12"></path>
                <path d="M9 21v-6h6v6"></path>
              </svg>
            </span>
            <span className="dash-banner__text">
              <span className="dash-banner__title">Start a new application</span>
              <span className="dash-banner__sub">Capture the lead, pick banks and complete the applicant details.</span>
            </span>
            <button type="button" className="dash-banner__cta" onClick={onCreateApplication}>
              <PlusIcon size={16} />
              Create Application
            </button>
          </div>

          <div className="tile-grid">{TILE_KEYS.map((key) => renderTile(key, buckets[key], filter, onFilterToggle))}</div>

          {/* Reportee Applications is SM-only (absent for every other role) and
              gets its own row rather than joining the fluid 3-tile grid above. */}
          {buckets["Reportee Applications"] && (
            <div className="tile-grid">{renderTile("Reportee Applications", buckets["Reportee Applications"], filter, onFilterToggle)}</div>
          )}

          <div className="lead-list-heading">
            {(q.trim() ? "Results - " : "All Leads - ") + leadCount}
            {filter !== "all" && (
              <button type="button" className="btn-link" style={{ marginLeft: 10, font: "600 12px/1 'Poppins'" }} onClick={() => onFilterToggle("all")}>
                Clear filter
              </button>
            )}
          </div>

          {leadsLoading && leads.length === 0 && <Spinner page label="Loading leads…" />}

          {!leadsLoading && leads.length === 0 && (
            <div className="empty-state">
              <span className="empty-state__icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8D8D8D" strokeWidth="1.7" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7"></circle>
                  <path d="m20 20-3.5-3.5"></path>
                </svg>
              </span>
              <div className="empty-state__text">
                {q.trim() ? `No lead matches "${q.trim()}". Try a Loan ID, customer name or source.` : "No leads in this bucket yet."}
              </div>
            </div>
          )}

          <div className="lead-grid">
            {leads.map((l) => {
              const isComplete = l.isCompleteApplicationCta;
              const actionable = !isComplete;
              const cta = isComplete ? "Complete Application" : l.statusId >= 2 ? "Track Application" : "Send to Bank";
              return (
                // Tapping the card anywhere but its CTA opens every tab view-only.
                <div
                  className="lead-card lead-card--clickable"
                  key={l.loan}
                  role="button"
                  tabIndex={0}
                  onClick={() => onContinueLead(l, { viewOnly: true })}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
                    e.preventDefault();
                    onContinueLead(l, { viewOnly: true });
                  }}
                >
                  <div className="lead-card__body">
                    <div className="lead-card__top">
                      <div className="lead-card__name">{l.name}</div>
                      <span className="lead-card__pill" style={{ background: pillBg(l.statusId, isComplete) }}>
                        {l.status}
                      </span>
                    </div>
                    <div className="lead-card__row">
                      <span className="lead-card__meta">Date : {l.date}</span>
                      <span className="lead-card__meta">Loan ID : {l.loan}</span>
                    </div>
                    <div className="lead-card__source">
                      Source : {l.sourceType}
                      {l.sourceName ? " - " + l.sourceName : ""}
                    </div>
                    <div className="lead-card__source">Submitted by : {l.submittedBy || "—"}</div>
                  </div>
                  {/* is_same_user is the master switch — a lead that isn't the
                      viewer's own (e.g. a reportee's) is view-only: it still
                      opens (read-only, via continueLead/isFrozen), just never
                      the actionable send-to-bank/track/complete flows. */}
                  <button
                    type="button"
                    className="lead-card__cta"
                    onClick={(e) => {
                      e.stopPropagation();
                      return !l.isSameUser
                        ? onContinueLead(l)
                        : actionable
                        ? onOpenLead({ loan: l.loan, rawId: l.rawId, name: l.name, stage: statusStage(l.statusId), tabStatus: l.financierTabStatus })
                        : onContinueLead(l);
                    }}
                  >
                    {l.isSameUser ? cta : "View"}
                  </button>
                </div>
              );
            })}
          </div>

          {hasMoreLeads && <div ref={sentinelRef} style={{ height: 1 }} />}
          {loadingMoreLeads && (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <Spinner />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
